import { motion } from "framer-motion";
import { SafeImage } from "@/components/ui/SafeImage";
import type { Partner } from "@/features/partners/types";
import { FeaturedPartnerBadge } from "./FeaturedPartnerBadge";

interface PartnerCardProps {
  partner: Partner;
}

export function PartnerCard({ partner }: PartnerCardProps) {
  return (
    <motion.article
      whileHover={{ y: -6 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={
        partner.featured
          ? "group h-full min-w-0 overflow-hidden rounded-lg border border-cyan-300/60 bg-black/70 shadow-neon transition hover:border-cyan-200"
          : "group h-full min-w-0 overflow-hidden rounded-lg border border-cyan-300/20 bg-black/60 shadow-[0_0_35px_rgba(34,211,238,0.08)] transition hover:border-cyan-300/70 hover:shadow-neon"
      }
    >
      <div className="flex h-full flex-col p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="grid size-20 shrink-0 place-items-center overflow-hidden rounded-md border border-cyan-300/20 bg-cyan-300/5">
            {partner.logoUrl ? (
              <SafeImage
                src={partner.logoUrl}
                alt={`${partner.name} logo`}
                fallbackLabel={partner.name.slice(0, 2)}
                className="h-full w-full object-contain p-2 transition duration-500 group-hover:scale-105"
                loading="lazy"
              />
            ) : (
              <span className="font-display text-3xl font-bold uppercase text-cyan-100/80">
                {partner.name.slice(0, 2)}
              </span>
            )}
          </div>
          {partner.featured ? <FeaturedPartnerBadge /> : null}
        </div>

        <h2 className="mt-5 break-words font-display text-xl font-bold uppercase text-white sm:text-2xl">{partner.name}</h2>
        <p className="mt-3 flex-1 text-sm leading-7 text-content-secondary">
          {partner.blurb || "Partner details are being prepared in the CMS."}
        </p>

        {partner.websiteUrl ? (
          <a
            href={partner.websiteUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex w-fit rounded border border-teal-300/40 bg-teal-300/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-teal-100 transition hover:border-cyan-300/70 hover:text-white hover:shadow-neon"
          >
            Visit Website
          </a>
        ) : null}
      </div>
    </motion.article>
  );
}
